/**
 * @fileoverview What a checkout says when it cannot render — shown in place of
 * the blank box a missing Stripe publishable key used to produce.
 *
 * This is a build misconfiguration, never anything the instructor did, so the
 * copy neither blames them nor asks them to retry something retrying cannot
 * fix. It states that the problem is ours, that nothing has been charged, and
 * gives the one action that actually moves it forward.
 *
 * One component, both checkout bodies, for the same reason
 * `SubscriptionComingSoon` is one component: two wordings for one condition is
 * two things to keep true.
 */

import { AlertCircle } from "lucide-react";
import { supportEmail } from "@/lib/companyBrand";

/**
 * @param {object} props
 * @param {string} [props.note] - Optional second line specific to the surface
 *   (e.g. that the current plan or minute balance is untouched).
 * @returns {React.ReactElement}
 */
const CheckoutUnavailable = ({ note }) => (
  <div
    data-testid="checkout-unavailable"
    className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-[var(--studio-divider)] bg-[var(--studio-raised)] px-5 py-10 text-center"
  >
    <div
      className="flex items-center justify-center rounded-full bg-[var(--state-danger-dim)] p-3"
      aria-hidden="true"
    >
      <AlertCircle className="h-5 w-5 text-[var(--state-danger)]" strokeWidth={1.5} />
    </div>

    <h2 className="font-serif text-lg font-light tracking-tight text-[var(--text-primary)] md:text-xl">
      Payments aren&rsquo;t available right now
    </h2>

    <p className="max-w-md text-sm text-[var(--text-secondary)]">
      This is a problem on our end, not yours, and nothing has been charged.
      Email us at{" "}
      <a
        href={`mailto:${supportEmail()}`}
        className="text-[var(--accent-yellow-text)] underline underline-offset-2"
      >
        {supportEmail()}
      </a>{" "}
      and we&rsquo;ll sort it out.
    </p>

    {note ? <p className="max-w-md text-xs text-[var(--text-tertiary)]">{note}</p> : null}
  </div>
);

export default CheckoutUnavailable;
